let createFFmpegFn = null
let fetchFileFn = null

/**
 * 动态加载 ffmpeg.min.js（CDN 版本），避免 webpack 打包 CommonJS 源码造成的兼容问题。
 */
function loadFfmpegScript () {
  return new Promise((resolve, reject) => {
    if (createFFmpegFn) return resolve()
    // 若全局已存在则直接取
    if (window.FFmpeg) {
      createFFmpegFn = window.FFmpeg.createFFmpeg
      fetchFileFn = window.FFmpeg.fetchFile
      return resolve()
    }
    const script = document.createElement('script')
    script.src = 'https://cdn.jsdelivr.net/npm/@ffmpeg/ffmpeg@0.10.0/dist/ffmpeg.min.js'
    script.onload = () => {
      createFFmpegFn = window.FFmpeg.createFFmpeg
      fetchFileFn = window.FFmpeg.fetchFile
      resolve()
    }
    script.onerror = reject
    document.head.appendChild(script)
  })
}

// 单例 FFmpeg 实例，避免重复加载
let _ffmpeg = null

/**
 * 确保 FFmpeg 已就绪
 * @returns {Promise<import('@ffmpeg/ffmpeg').FFmpeg>} ffmpeg 实例
 */
export async function ensureFFmpeg () {
  if (!_ffmpeg) {
    await loadFfmpegScript()
    _ffmpeg = createFFmpegFn({
      log: false,
      // 指向单线程构建，避免浏览器要求 SharedArrayBuffer
      corePath: 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.10.0/dist/ffmpeg-core.js'
    })
    await _ffmpeg.load()
    // 开启全局日志
    _ffmpeg.setLogging(true)
  }
  return _ffmpeg
}

/**
 * 将用户上传的音频文件转码为符合 p3 协议的 Uint8Array
 * @param {File|Blob|ArrayBuffer} file - 原始音频文件
 * @returns {Promise<Uint8Array>} p3 数据
 */
export async function transcodeToP3 (file) {
  const ffmpeg = await ensureFFmpeg()

  // 注册一次 logger 打印 FFmpeg 输出与进度
  ffmpeg.setLogger(({ type, message }) => {
    // 仅开发环境打印，生产可去掉
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[ffmpeg:${type}]`, message)
    }
  })
  ffmpeg.setProgress(({ ratio, time }) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[ffmpeg:progress]', (ratio * 100).toFixed(2) + '%', time || '')
    }
  })

  // 写入虚拟文件系统
  await ffmpeg.FS('writeFile', 'input', await fetchFileFn(file))

  // 尝试 data 裸流；若失败则回退到 opus
  const cmd = [
    '-i', 'input',
    '-map', '0:a:0', // 仅输出首条音频流，防止 data muxer 无流
    '-ar', '16000', '-ac', '1',
    '-c:a', 'libopus',
    '-b:a', '24k',
    '-frame_duration', '60',
    '-vbr', 'off', '-application', 'audio',
    '-f', 'opus', '/out.opus'
  ]

  await ffmpeg.run(...cmd)

  // 调试：输出文件大小
  try {
    const stat = ffmpeg.FS('stat', '/out.opus')
    console.log('[ffmpeg] /out.opus size', stat.size, 'bytes')
  } catch (e) {
    console.warn('stat /out.opus failed', e)
  }

  const oggBuf = ffmpeg.FS('readFile', '/out.opus') // Uint8Array
  return oggToP3(oggBuf)
}

// 解析 Ogg/Opus 容器并封装为 p3
export function oggToP3 (u8) {
  const frames = []
  let pos = 0
  let packetIndex = 0
  while (pos + 27 <= u8.length) {
    if (u8[pos] !== 0x4f || u8[pos + 1] !== 0x67 || u8[pos + 2] !== 0x67 || u8[pos + 3] !== 0x53) {
      break // 非 OggS 标识
    }
    const pageSegments = u8[pos + 26]
    if (pos + 27 + pageSegments > u8.length) break
    const segTable = u8.slice(pos + 27, pos + 27 + pageSegments)
    let segPtr = pos + 27 + pageSegments
    let packet = []
    for (let i = 0; i < pageSegments; i++) {
      const segLen = segTable[i]
      if (segPtr + segLen > u8.length) break
      const segData = u8.slice(segPtr, segPtr + segLen)
      packet.push(...segData)
      segPtr += segLen
      if (segLen < 255) {
        // 一个完整 packet 结束
        // 跳过前两个非音频配置帧（OpusHead 与 OpusTags）
        if (packetIndex >= 2) {
          frames.push(new Uint8Array(packet))
        }
        packetIndex++
        packet = []
      }
    }
    pos = segPtr
  }

  // 封装 p3
  const chunks = []
  for (const frame of frames) {
    const len = frame.length
    chunks.push(0x00, 0x00, (len >> 8) & 0xff, len & 0xff)
    chunks.push(...frame)
  }
  return new Uint8Array(chunks)
}
